import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import { EmailCard } from '@/components/ui/email-card';
import { Spinner } from '@/components/ui/spinner';
import type { EmailItem } from '@/config/office/types';

import { emailItemLayoutId, emailItemTransition, filterOptionLabelClass } from './constants';

interface InboxEmailsListProps {
  debouncedSearch: string;
  emails: Array<EmailItem>;
  error: Error | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  onToggleEmail: (id: string) => void;
  selectedEmailIds: Set<string>;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  showCheckboxes: boolean;
}

export function InboxEmailsList({
  debouncedSearch,
  emails,
  error,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  onToggleEmail,
  selectedEmailIds,
  sentinelRef,
  showCheckboxes,
}: InboxEmailsListProps) {
  const { t } = useTranslation();

  const visibleEmails = emails.filter((email) => !selectedEmailIds.has(email.id));

  return (
    <motion.div layout transition={emailItemTransition}>
      <div className="px-3 pt-2 pb-1">
        <h3 className={`mb-1 ${filterOptionLabelClass}`}>{t('assistant.emailContext.inbox')}</h3>
      </div>

      <div className="flex flex-col gap-1 px-3 pb-3">
        {isLoading && (
          <div className="text-neutral-mid px-3 py-4 text-center text-sm">
            {t('assistant.emailContext.loading')}
          </div>
        )}
        {error && (
          <div className="text-destructive px-3 py-4 text-center text-sm">
            {t('assistant.emailContext.error')}
          </div>
        )}
        {!isLoading && !error && visibleEmails.length === 0 && (
          <div className="text-neutral-mid px-3 py-4 text-center text-sm">
            {debouncedSearch
              ? t('assistant.emailContext.noResultsForSearch')
              : t('assistant.emailContext.noEmails')}
          </div>
        )}
        {!isLoading &&
          !error &&
          visibleEmails.map((email, index) => (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 4 }}
              key={email.id}
              layout
              layoutId={emailItemLayoutId(email.id)}
              transition={{ ...emailItemTransition, delay: Math.min(index * 0.015, 0.12) }}
            >
              <EmailCard
                id={email.id}
                sender={email.sender}
                senderColor={email.senderColor}
                senderInitial={email.senderInitial}
                subject={email.subject}
                time={email.time}
                {...(email.snippet && { snippet: email.snippet })}
                isDisabled={false}
                onClick={() => onToggleEmail(email.id)}
                searchQuery={debouncedSearch}
                showCheckboxes={showCheckboxes}
              />
            </motion.div>
          ))}
        {hasNextPage && <div className="h-2 w-full" ref={sentinelRef} />}
        {isFetchingNextPage && (
          <div className="flex justify-center py-3">
            <Spinner className="text-neutral-mid size-5" />
          </div>
        )}
      </div>
    </motion.div>
  );
}
